/**
 * Enhanced API Client with Robust Token Management and Request Deduplication
 *
 * DESIGN PRINCIPLES:
 * 1. Proactive token refresh before expiration
 * 2. Automatic retry on 401 with cache clear
 * 3. Clear error messages with actionable context
 * 4. Token validation and scope verification
 * 5. Comprehensive logging for debugging
 * 6. Request deduplication for GET requests to prevent duplicate API calls
 */

import axios from 'axios';
import { msalInstance } from '../auth/msalInstance.js';
import { apiRequest } from '../utils/authConfig.js';
import Logger from '../utils/logger.js';
import requestDeduplicator from '../utils/requestDeduplicator.js';
import { signRequest, isHmacSigningEnabled, getHmacSigningKey } from '../utils/hmacSigning.js';
import { generateCorrelationId, getSessionCorrelationId } from '../utils/correlationId.js';

// Lazy-initialized axios instance
let _axiosInstance = null;

// CSRF token storage (set by CSRFProvider or fetched directly)
let _csrfToken = null;
let _csrfTokenFetchPromise = null;

// CSRF configuration
const CSRF_HEADER_NAME = 'x-csrf-token';
const CSRF_PROTECTED_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH'];
const CSRF_MAX_RETRIES = 3;
const CSRF_RETRY_BASE_DELAY_MS = 300;

/**
 * Set the CSRF token for subsequent requests
 * Called by CSRFProvider when token is fetched
 * @param {string} token - CSRF token
 */
export const setCSRFToken = token => {
  _csrfToken = token;
  Logger.debug('[API CLIENT] CSRF token updated');
};

/**
 * Get the current CSRF token
 * @returns {string|null} Current CSRF token
 */
export const getCSRFToken = () => _csrfToken;

/**
 * Fetch CSRF token from the server.
 *
 * - Singleton: concurrent callers share the same in-flight Promise, so
 *   only one network request is made regardless of how many callers arrive
 *   while the request is pending.
 * - Retry: up to CSRF_MAX_RETRIES attempts with exponential back-off before
 *   propagating a failure to all waiting callers.
 * - Safe clear: `_csrfTokenFetchPromise` is reassigned to the result of an
 *   outer `.finally()` call so the reference is nulled AFTER the in-flight
 *   Promise settles (success or failure). Concurrent callers awaiting the
 *   same promise will receive the resolved token or the thrown error.
 *
 * @returns {Promise<string>} CSRF token
 */
export const fetchCSRFToken = async () => {
  // Return existing token if available
  if (_csrfToken) {
    return _csrfToken;
  }

  // Return pending fetch if one is in progress (deduplication)
  if (_csrfTokenFetchPromise) {
    return _csrfTokenFetchPromise;
  }

  // Start new fetch with retry
  _csrfTokenFetchPromise = (async () => {
    let lastError;
    for (let attempt = 0; attempt <= CSRF_MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          // Exponential back-off: 300 ms, 600 ms, 1200 ms
          await new Promise(resolve => setTimeout(resolve, CSRF_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1)));
          Logger.info(`[API CLIENT] Retrying CSRF token fetch (attempt ${attempt + 1})`);
        }
        const instance = getInstance();
        // GET requests don't require CSRF validation per HTTP spec (safe method)
        const response = await instance.get('/csrf-token');
        _csrfToken = response.data.token;
        Logger.info('[API CLIENT] CSRF token fetched successfully');
        return _csrfToken;
      } catch (error) {
        const status = error.response?.status;
        const isNetworkError = !error.response;
        const isRetryableStatus = typeof status === 'number' && (status >= 500 || status === 429);

        Logger.warn(`[API CLIENT] CSRF token fetch attempt ${attempt + 1} failed:`, {
          message: error.message,
          status
        });

        if (!isNetworkError && !isRetryableStatus) {
          // Fail fast for non-retryable errors (auth failures, 4xx other than 429)
          Logger.error('[API CLIENT] Non-retryable error while fetching CSRF token, aborting retries:', {
            message: error.message,
            status
          });
          throw error;
        }

        lastError = error;
      }
    }
    Logger.error('[API CLIENT] Failed to fetch CSRF token after all retries:', {
      message: lastError?.message
    });
    throw lastError;
  })();
  // Reassign to the .finally() result so the reference is cleaned up whether
  // the IIFE resolves or rejects, and callers awaiting this promise receive
  // the resolved token or the thrown error without an unobserved rejection.
  _csrfTokenFetchPromise = _csrfTokenFetchPromise.finally(() => {
    _csrfTokenFetchPromise = null;
  });

  return _csrfTokenFetchPromise;
};

/**
 * Clear the CSRF token (e.g., on logout or 403 error)
 */
export const clearCSRFToken = () => {
  _csrfToken = null;
  _csrfTokenFetchPromise = null;
  Logger.debug('[API CLIENT] CSRF token cleared');
};

// Track token refresh state — use promise as the sole gate to prevent races
let tokenRefreshPromise = null;

// Constants
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiration

/**
 * Get or create the axios instance
 */
const getInstance = () => {
  if (_axiosInstance) return _axiosInstance;

  _axiosInstance = axios.create({
    baseURL: (() => {
      const url = import.meta.env.VITE_API_BASE_URL;
      if (url) return url;
      if (import.meta.env.DEV) {
        Logger.warn('[apiClient] VITE_API_BASE_URL not set, using localhost fallback');
        return 'http://localhost:7071/api';
      }
      throw new Error(
        '[apiClient] VITE_API_BASE_URL is required in non-development environments. ' +
          'Set this environment variable before building or starting the application.'
      );
    })(),
    timeout: 45000, // Increased from 30s to accommodate slow Cosmos DB queries
    withCredentials: true, // Required to send cookies in cross-origin requests (CSRF protection)
    headers: {
      'Content-Type': 'application/json'
    }
  });

  return _axiosInstance;
};

/**
 * Check if token needs refresh based on expiration time
 */
const shouldRefreshToken = expiresOn => {
  if (!expiresOn) return false;
  const expirationTime = new Date(expiresOn).getTime();
  const now = Date.now();
  const timeUntilExpiry = expirationTime - now;
  return timeUntilExpiry < TOKEN_REFRESH_BUFFER_MS;
};

/**
 * Validate token has correct scopes
 *
 * NOTE: For self-service APIs (SPA calling its own backend), Azure AD may not
 * include custom scopes in the token's scope claim. The API permission is validated
 * through the audience (aud) claim instead. The backend validates the token's
 * audience and signature, which is sufficient for authorization.
 *
 * We keep basic validation here for logging purposes, but always return true
 * to allow the token through. The backend is the authoritative validator.
 */
const validateTokenScopes = (requestedScope, receivedScopes) => {
  // Log what scopes we received for debugging
  if (!receivedScopes || receivedScopes.length === 0) {
    Logger.debug('[API CLIENT] No scopes in token response - backend will validate via audience claim');
  } else {
    Logger.debug('[API CLIENT] Token scopes received:', receivedScopes);
  }

  // Always return true - backend validates the token
  // Frontend scope checking is informational only
  return true;
};

/**
 * Acquire access token with comprehensive error handling
 */
const acquireAccessToken = async (account, forceRefresh = false) => {
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account,
      forceRefresh
    });

    if (!response?.accessToken) {
      throw new Error('No access token in response');
    }

    const requestedScope = apiRequest.scopes[0];
    const receivedScopes = response.scopes || [];

    Logger.debug('[API CLIENT] Token acquired', {
      requestedScope,
      receivedScopes,
      expiresOn: response.expiresOn,
      forceRefresh
    });

    // Validate scopes (informational only - backend does authoritative validation)
    validateTokenScopes(requestedScope, receivedScopes);

    // Check if token needs proactive refresh
    if (!forceRefresh && shouldRefreshToken(response.expiresOn)) {
      Logger.info('[API CLIENT] Token expiring soon, proactively refreshing');
      return acquireAccessToken(account, true);
    }

    return response.accessToken;
  } catch (error) {
    Logger.error('[API CLIENT] Token acquisition failed:', {
      error: error.message,
      errorCode: error.errorCode,
      forceRefresh
    });
    throw error;
  }
};

/**
 * Setup API client with authentication interceptor
 * Call this once during app initialization
 */
let isSetupComplete = false;

export const setupApiClient = () => {
  if (isSetupComplete) {
    Logger.debug('[API CLIENT] Already configured');
    return;
  }

  const instance = getInstance();

  // Request interceptor - add access token to all API requests
  instance.interceptors.request.use(
    async config => {
      // Generate unique correlation ID for request tracing (per-request)
      const correlationId = generateCorrelationId();
      config.headers['x-correlation-id'] = correlationId;

      // Get or create session ID (persists across requests in same browser tab)
      const sessionId = getSessionCorrelationId();
      config.headers['x-session-id'] = sessionId;

      // Store for use in response interceptor
      config.metadata = {
        ...(config.metadata || {}),
        correlationId,
        sessionId,
        startTime: Date.now()
      };

      Logger.debug(
        '[API Request]',
        {
          method: config.method,
          url: config.url
        },
        { correlationId, sessionId }
      );

      // Skip auth for health check
      if (config.url?.includes('/health')) {
        Logger.debug('[API CLIENT] Skipping auth for health check', { correlationId });
        return config;
      }

      // Generate request key for deduplication (GET requests only)
      if (config.method?.toLowerCase() === 'get') {
        const requestKey = `${config.method}:${config.url}:${JSON.stringify(config.params || {})}`;
        config.metadata.requestKey = requestKey;
        Logger.debug('[API CLIENT] Request key generated for deduplication:', {
          requestKey,
          correlationId
        });
      }

      // Get all accounts
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length === 0) {
        Logger.warn('[API CLIENT] No accounts found - user not authenticated');
        return config;
      }

      try {
        // Prevent concurrent token refreshes — reuse in-flight promise
        if (!tokenRefreshPromise) {
          tokenRefreshPromise = acquireAccessToken(accounts[0]).finally(() => {
            tokenRefreshPromise = null;
          });
        }

        const accessToken = await tokenRefreshPromise;
        config.headers.Authorization = `Bearer ${accessToken}`;
        Logger.debug('[API CLIENT] Access token added to request');

        // Add CSRF token for state-changing requests
        const method = config.method?.toUpperCase();
        if (CSRF_PROTECTED_METHODS.includes(method)) {
          if (_csrfToken) {
            config.headers[CSRF_HEADER_NAME] = _csrfToken;
            Logger.debug('[API CLIENT] CSRF token added to request');
          } else {
            // Try to fetch CSRF token if not available
            try {
              const token = await fetchCSRFToken();
              if (token) {
                config.headers[CSRF_HEADER_NAME] = token;
                Logger.debug('[API CLIENT] CSRF token fetched and added to request');
              }
            } catch (csrfError) {
              Logger.warn('[API CLIENT] Could not fetch CSRF token:', csrfError.message);
              // Continue without CSRF token - backend will reject if required
            }
          }
        }

        // Add HMAC signature if enabled
        if (isHmacSigningEnabled()) {
          const signingKey = getHmacSigningKey();
          if (signingKey) {
            try {
              await signRequest(config, signingKey);
              Logger.debug('[API CLIENT] HMAC signature added to request');
            } catch (signError) {
              Logger.error('[API CLIENT] Failed to sign request with HMAC:', signError);
              // Continue without signature - backend may not require it
            }
          } else {
            Logger.warn('[API CLIENT] HMAC signing enabled but no key configured');
          }
        }
      } catch (error) {
        tokenRefreshPromise = null;

        Logger.error('[API CLIENT] Failed to acquire access token:', {
          error: error.message,
          errorCode: error.errorCode,
          errorName: error.name
        });

        // If interaction is required, trigger an interactive login
        if (
          error.errorCode === 'consent_required' ||
          error.errorCode === 'interaction_required' ||
          error.errorCode === 'login_required'
        ) {
          Logger.warn('[API CLIENT] Interactive login required, triggering redirect');
          msalInstance.acquireTokenRedirect({
            ...apiRequest,
            account: accounts[0]
          });
        }

        // Enhanced error message
        const enhancedError = new Error(
          `Authentication failed: ${error.errorCode || error.message}. Please try logging out and back in.`
        );
        enhancedError.originalError = error;
        enhancedError.isAuthError = true;
        throw enhancedError;
      }

      return config;
    },
    error => {
      Logger.error('[API CLIENT] Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle 401 errors with retry
  instance.interceptors.response.use(
    response => {
      const { correlationId, startTime, requestKey } = response.config?.metadata || {};
      const duration = startTime ? Date.now() - startTime : null;

      Logger.debug(
        '[API Response]',
        {
          endpoint: response.config?.url,
          status: response.status,
          duration
        },
        { correlationId }
      );

      // Clear request from deduplication cache on successful response
      if (requestKey) {
        // Note: We don't clear the cache here - let the TTL handle it
        // This allows subsequent requests to benefit from the cache
        Logger.debug('[API CLIENT] Request completed successfully:', {
          requestKey,
          correlationId
        });
      }
      return response;
    },
    async error => {
      const originalRequest = error.config;
      const { correlationId, startTime } = originalRequest?.metadata || {};
      const duration = startTime ? Date.now() - startTime : null;

      // Detect if the error response is HTML instead of JSON (common with 504 Gateway Timeout)
      const responseData = error.response?.data;
      const isString = typeof responseData === 'string';
      const isHtmlResponse =
        error.response?.headers?.['content-type']?.includes('text/html') ||
        (isString && responseData.trim().startsWith('<!DOCTYPE')) ||
        (isString && responseData.trim().startsWith('<html'));

      // Enhance error message for HTML responses
      let errorMessage = error.message;
      if (isHtmlResponse) {
        if (error.response?.status === 504) {
          errorMessage = 'Gateway timeout - the server took too long to respond. Please try again.';
          error.isTimeout = true;
        } else if (error.response?.status === 502) {
          errorMessage = 'Bad gateway - the server is temporarily unavailable. Please try again.';
        } else {
          const status = error.response?.status;
          errorMessage = `Server error (${status}) - received unexpected HTML response. Please try again.`;
        }
        error.isHtmlError = true;
        error.userMessage = errorMessage;
      }

      // Log requestId from response body to console for App Insights correlation.
      // This is a developer aid only — do NOT surface requestId in the user UI.
      // responseData is already declared above when checking for HTML responses.
      const responseRequestId =
        typeof responseData === 'object' && responseData !== null
          ? (responseData.requestId ?? responseData.error?.requestId ?? null)
          : null;
      if (responseRequestId) {
        Logger.debug(`[API Error] requestId: ${responseRequestId} — use this in App Insights to find the trace`);
      }

      Logger.error(
        '[API Error]',
        {
          endpoint: originalRequest?.url,
          status: error.response?.status,
          duration,
          errorMessage,
          isHtmlResponse,
          requestId: responseRequestId ?? undefined
        },
        { correlationId }
      );

      if (error.response?.status === 401 && !originalRequest._retry) {
        Logger.warn('[API CLIENT] Received 401 Unauthorized, attempting token refresh and retry', { correlationId });

        originalRequest._retry = true;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) {
          Logger.error('[API CLIENT] Cannot retry - no accounts found');
          error.isAuthError = true;
          error.userMessage = 'Your session has expired. Please log in again.';
          return Promise.reject(error);
        }

        try {
          // Clear token cache and force refresh
          Logger.info('[API CLIENT] Clearing token cache and forcing refresh');
          const accessToken = await acquireAccessToken(accounts[0], true);

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;

          // Re-sign request with HMAC if enabled
          if (isHmacSigningEnabled()) {
            const signingKey = getHmacSigningKey();
            if (signingKey) {
              try {
                await signRequest(originalRequest, signingKey);
                Logger.debug('[API CLIENT] HMAC signature added to retry request');
              } catch (signError) {
                Logger.error('[API CLIENT] Failed to sign retry request with HMAC:', signError);
              }
            }
          }

          Logger.info('[API CLIENT] Retrying request with refreshed token');
          return instance(originalRequest);
        } catch (retryError) {
          Logger.error('[API CLIENT] Token refresh and retry failed:', retryError);
          retryError.isAuthError = true;
          retryError.userMessage =
            'Your session has expired and could not be refreshed. Please log out and log in again.';
          return Promise.reject(retryError);
        }
      }

      // Handle 403 CSRF validation errors with retry
      if (
        error.response?.status === 403 &&
        error.response?.data?.code === 'CSRF_VALIDATION_FAILED' &&
        !originalRequest._csrfRetry
      ) {
        Logger.warn('[API CLIENT] CSRF validation failed, refreshing token and retrying');
        originalRequest._csrfRetry = true;

        try {
          // Clear existing token and fetch new one
          clearCSRFToken();
          const newToken = await fetchCSRFToken();

          if (newToken) {
            originalRequest.headers[CSRF_HEADER_NAME] = newToken;
            Logger.info('[API CLIENT] Retrying request with new CSRF token');
            return instance(originalRequest);
          }
        } catch (csrfError) {
          Logger.error('[API CLIENT] CSRF token refresh failed:', csrfError);
        }

        error.isCSRFError = true;
        error.userMessage = 'Security validation failed. Please refresh the page and try again.';
        return Promise.reject(error);
      }

      // Add user-friendly messages to errors
      if (error.response?.status === 401) {
        error.isAuthError = true;
        error.userMessage = 'Authentication failed. Please log out and log in again.';
      } else if (error.response?.status === 403) {
        // Check if it's a CSRF error (already handled above) vs permission error
        if (error.response?.data?.code === 'CSRF_VALIDATION_FAILED') {
          error.isCSRFError = true;
          error.userMessage = 'Security validation failed. Please refresh the page and try again.';
        } else {
          error.isAuthError = true;
          error.userMessage = 'You do not have permission to perform this action.';
        }
      }

      // Clear request from deduplication on error
      const requestKey = error.config?.metadata?.requestKey;
      if (requestKey) {
        // Clear from deduplicator to allow retry
        Logger.debug('[API CLIENT] Clearing failed request from deduplication:', requestKey);
        requestDeduplicator.clearEndpoint(requestKey);
      }

      return Promise.reject(error);
    }
  );

  isSetupComplete = true;
  Logger.info('[API CLIENT] Configuration complete with enhanced token management');
};

/**
 * Get the configured axios instance
 */
export const getApiClient = () => {
  if (!isSetupComplete) {
    setupApiClient();
  }
  return getInstance();
};

// Named export for backward compatibility
export const apiClient = getApiClient();

/**
 * Acquire access token for WebSocket or other non-HTTP uses
 * @returns {Promise<string>} Access token
 */
export const getAccessToken = async () => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No authenticated account found');
  }
  return acquireAccessToken(accounts[0], false);
};

// Default export
export default apiClient;
